import { defineStore } from "pinia";
import { BatchExport } from "./entity";
import { BatchExportList } from "./list.gen";
import type { BatchExportStore, BatchExportStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const batchExportList = new BatchExportList();
export const useBatchExportStore = defineStore("batchExportStore", {
  state: (): BatchExportStoreState => ({
    activeID: null,
    cache: {},
    list: [] as BatchExport[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-BatchExport", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.BatchExport"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(batchExport: BatchExport): Promise<void> {
      // Set active
      await batchExport
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = batchExport.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(batchExport: BatchExport): void {
      const id = batchExport.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(batchExport);

        // Set active
        if (this.active === null) {
          this.activeID = batchExport.id;
        }
      } else {
        this.reinsert(batchExport);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as BatchExport[]).map((x: any) => {
        const batchExport = new BatchExport({});
        batchExport.set(x as JSONData);
        return batchExport;
      });
    },
    async delete(batchExport: BatchExport): Promise<void> {
      await batchExport
        .delete()
        .then(() => {
          this.remove(batchExport);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const batchExport = (this.list as BatchExport[]).find(
        (x: BatchExport) => x.id === id,
      );
      if (batchExport !== undefined) {
        await this.delete(batchExport);
      } else {
        throw new Error("Could not find BatchExport with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as BatchExport[]).some((x: BatchExport) => x.id === id);
    },
    get(id: string): BatchExport | null {
      return (
        (this.list as BatchExport[]).find((x: BatchExport) => x.id === id) ||
        null
      );
    },
    reinsert(batchExport: BatchExport): void {
      const index = (this.list as BatchExport[]).findIndex(
        (x: BatchExport) => x.id === batchExport.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          batchExport,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(batchExport);
      }
      this.updateKey += 1;
    },
    remove({ id }: BatchExport): void {
      const index = (this.list as BatchExport[]).findIndex(
        (x: BatchExport) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<BatchExport> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const batchExport = this.get(id);
        if (batchExport !== null) {
          return batchExport;
        }
      }

      // Retrieve batchExport
      const batchExport = new BatchExport({});

      // Get batchExport
      await batchExport.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate batchExport
        await this.activate(entity);
      });

      return batchExport;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: BatchExport[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as BatchExport[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await batchExportList.getAll().then(async (list) => {
        list.forEach((batchExport) => {
          this.reinsert(batchExport);
        });

        // Activate batchExport
        if (list.length > 0) {
          if (batchExportList.active !== null) {
            this.activeID = batchExportList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("batch_export_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as BatchExport[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: BatchExport[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as BatchExport),
        };
      }

      // Retrieve list
      const items = await batchExportList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((batchExport) => {
            this.reinsert(batchExport);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: BatchExport) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: BatchExport[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as BatchExport),
        };
      }

      // Retrieve list
      const items = await batchExportList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((batchExport) => {
            this.reinsert(batchExport);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: BatchExport) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: BatchExport[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as BatchExport),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await batchExportList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((batchExport) => {
            this.reinsert(batchExport);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: BatchExport) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(batchExport: BatchExport): Promise<string> {
      // Update batchExport
      await batchExport.update();

      // Reinsert batchExport
      this.reinsert(batchExport);

      // Activate batchExport
      if (this.active === null) {
        await this.activate(batchExport);
      }

      this.emitStoreUpdateEvent();

      return batchExport.id;
    },
  },
  getters: {
    active(): BatchExport | null {
      if (this.activeID === null) {
        return null;
      }

      const batchExport = (this.list as BatchExport[]).find(
        (x: BatchExport) => x.id === this.activeID,
      );
      if (batchExport === undefined) {
        return null;
      }

      return batchExport;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createBatchExportStore(): BatchExportStore {
  const store = useBatchExportStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as BatchExportStore; // TODO: Fix this
}
